/**
 * Currency Formatter
 * Wraps Intl.NumberFormat for currency formatting
 * @module @infra/format
 */

import type { CurrencyFormatOptions } from './format.types';

const formattersCache = new Map<string, Intl.NumberFormat>();

/**
 * Generate cache key for formatter
 */
function getCacheKey(locale: string, options: CurrencyFormatOptions): string {
  return `${locale}:${options.currency}:${options.minimumFractionDigits ?? ''}:${options.notation ?? ''}`;
}

/**
 * Format number as currency
 * Caches formatters for performance
 * @param value - Number to format
 * @param locale - BCP 47 language tag (e.g., 'vi', 'en')
 * @param options - Currency options
 * @returns Formatted currency string (e.g., "₫1,000,000" for VND)
 */
export function formatCurrency(
  value: number,
  locale: string,
  options: CurrencyFormatOptions
): string {
  const cacheKey = getCacheKey(locale, options);

  if (!formattersCache.has(cacheKey)) {
    const intlOptions: Intl.NumberFormatOptions = {
      style: 'currency',
      currency: options.currency,
      minimumFractionDigits: options.minimumFractionDigits ?? (options.currency === 'VND' ? 0 : 2),
      maximumFractionDigits: options.maximumFractionDigits ?? (options.currency === 'VND' ? 0 : 2),
      useGrouping: options.useGrouping ?? true,
      notation: options.notation ?? 'standard',
    };
    formattersCache.set(cacheKey, new Intl.NumberFormat(locale, intlOptions));
  }

  return formattersCache.get(cacheKey)!.format(value);
}
